import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Building2, BedDouble, DoorOpen, Copy, Layers } from 'lucide-react';
import BulkRoomModal from '../components/BulkRoomModal';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { selectedProperty, refreshProperties } = useOutletContext();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [unitTypeDialogOpen, setUnitTypeDialogOpen] = useState(false);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [bulkRoomDialogOpen, setBulkRoomDialogOpen] = useState(false);

  // Edit states
  const [editPropertyId, setEditPropertyId] = useState(null);
  const [editUnitTypeId, setEditUnitTypeId] = useState(null);
  const [editUnitId, setEditUnitId] = useState(null);

  // Form data
  const [propertyForm, setPropertyForm] = useState({ 
    name: '', 
    address: '', 
    phone: '', 
    email: '',
    tax_state_rate: '',
    tax_city_rate: '',
    tax_county_rate: '',
    tax_tourist_rate: ''
  });
  const [unitTypeForm, setUnitTypeForm] = useState({ 
    name: '', 
    capacity: 2, 
    bed_type: '', 
    tags: ''
  });
  const [unitForm, setUnitForm] = useState({ 
    unit_type_id: '', 
    name: '', 
    floor: 1,
    smoking_policy: 'NON_SMOKING'
  });

  useEffect(() => {
    fetchData();
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const timestamp = Date.now(); // Cache buster
      
      const propsRes = await axios.get(`${API}/properties?_t=${timestamp}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      setProperties(propsRes.data);

      if (selectedProperty) {
        console.log('[Settings] Fetching data for property:', selectedProperty.id);
        const [typesRes, unitsRes] = await Promise.all([
          axios.get(`${API}/properties/${selectedProperty.id}/unit-types?_t=${timestamp}`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache'
            }
          }),
          axios.get(`${API}/properties/${selectedProperty.id}/units?_t=${timestamp}`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache'
            }
          })
        ]);
        console.log('[Settings] Unit types fetched:', typesRes.data.length);
        console.log('[Settings] Units fetched:', unitsRes.data.length);
        setUnitTypes(typesRes.data);
        setUnits(unitsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      
      // Prepare property data - convert tax fields to numbers
      const propertyData = {
        ...propertyForm,
        tax_state_rate: propertyForm.tax_state_rate ? parseFloat(propertyForm.tax_state_rate) : null,
        tax_city_rate: propertyForm.tax_city_rate ? parseFloat(propertyForm.tax_city_rate) : null,
        tax_county_rate: propertyForm.tax_county_rate ? parseFloat(propertyForm.tax_county_rate) : null,
        tax_tourist_rate: propertyForm.tax_tourist_rate ? parseFloat(propertyForm.tax_tourist_rate) : null
      };
      
      if (editPropertyId) {
        // Update existing property
        await handleUpdateProperty(e);
        return;
      }
      
      await axios.post(`${API}/properties`, propertyData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property created successfully');
      setPropertyDialogOpen(false);
      setPropertyForm({ name: '', address: '', phone: '', email: '', tax_state_rate: '', tax_city_rate: '', tax_county_rate: '', tax_tourist_rate: '' });
      fetchData();
      refreshProperties();
    } catch (error) {
      console.error('Create property error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create property');
    }
  };

  const handleCreateUnitType = async (e) => {
    e.preventDefault();
    if (!selectedProperty) return;

    try {
      const token = localStorage.getItem('access_token');
      const tags = unitTypeForm.tags.split(',').map(t => t.trim()).filter(t => t);
      
      if (editUnitTypeId) {
        // Update existing
        await handleUpdateUnitType(e);
        return;
      }
      
      await axios.post(
        `${API}/properties/${selectedProperty.id}/unit-types`,
        { ...unitTypeForm, tags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room type created successfully');
      setUnitTypeDialogOpen(false);
      setUnitTypeForm({ name: '', capacity: 2, bed_type: '', tags: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create room type');
    }
  };

  const handleCreateUnit = async (e) => {
    e.preventDefault();
    if (!selectedProperty) return;

    try {
      const token = localStorage.getItem('access_token');
      
      if (editUnitId) {
        // Update existing
        await handleUpdateUnit(e);
        return;
      }
      
      await axios.post(
        `${API}/properties/${selectedProperty.id}/units`,
        unitForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room created successfully');
      setUnitDialogOpen(false);
      setUnitForm({ unit_type_id: '', name: '', floor: 1 });
      fetchData();
    } catch (error) {
      console.error('Create room error:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Failed to create room';
      // Show user-friendly error for duplicates
      if (errorMessage.includes('already exists')) {
        toast.error(`Error: Room ${unitForm.name} already exists`);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  // Property Update/Delete handlers
  const handleEditProperty = (property) => {
    setEditPropertyId(property.id);
    setPropertyForm({
      name: property.name,
      address: property.address,
      phone: property.phone,
      email: property.email,
      tax_state_rate: property.tax_state_rate || '',
      tax_city_rate: property.tax_city_rate || '',
      tax_county_rate: property.tax_county_rate || '',
      tax_tourist_rate: property.tax_tourist_rate || ''
    });
    setPropertyDialogOpen(true);
  };

  const handleUpdateProperty = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      
      console.log('[Settings] Updating property:', editPropertyId);
      console.log('[Settings] Raw form data:', propertyForm);
      
      // Prepare data - convert tax fields to numbers
      const updateData = {
        ...propertyForm,
        tax_state_rate: propertyForm.tax_state_rate ? parseFloat(propertyForm.tax_state_rate) : null,
        tax_city_rate: propertyForm.tax_city_rate ? parseFloat(propertyForm.tax_city_rate) : null,
        tax_county_rate: propertyForm.tax_county_rate ? parseFloat(propertyForm.tax_county_rate) : null,
        tax_tourist_rate: propertyForm.tax_tourist_rate ? parseFloat(propertyForm.tax_tourist_rate) : null
      };
      
      console.log('[Settings] Sending update data:', updateData);
      
      const response = await axios.patch(`${API}/properties/${editPropertyId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Settings] Update response:', response.data);
      toast.success('Property updated successfully');
      
      // Wait a moment before refreshing to ensure database has updated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setPropertyDialogOpen(false);
      setEditPropertyId(null);
      setPropertyForm({ name: '', address: '', phone: '', email: '', tax_state_rate: '', tax_city_rate: '', tax_county_rate: '', tax_tourist_rate: '' });
      
      // Refresh data
      await fetchData();
      await refreshProperties();
      
    } catch (error) {
      console.error('[Settings] Update property error:', error);
      console.error('[Settings] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyId, propertyName) => {
    if (!window.confirm(`Are you sure you want to delete "${propertyName}"? This will remove it from the calendar and all views.`)) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property deleted successfully');
      fetchData();
      refreshProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  // Unit Type Update/Delete handlers
  const handleEditUnitType = (unitType) => {
    setEditUnitTypeId(unitType.id);
    setUnitTypeForm({
      name: unitType.name,
      capacity: unitType.capacity,
      bed_type: unitType.bed_type,
      tags: unitType.tags.join(', ')
    });
    setUnitTypeDialogOpen(true);
  };

  const handleUpdateUnitType = async (e) => {
    e.preventDefault();
    if (!selectedProperty) return;

    try {
      const token = localStorage.getItem('access_token');
      const tags = unitTypeForm.tags.split(',').map(t => t.trim()).filter(t => t);
      await axios.patch(
        `${API}/properties/${selectedProperty.id}/unit-types/${editUnitTypeId}`,
        { ...unitTypeForm, tags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room type updated successfully');
      setUnitTypeDialogOpen(false);
      setEditUnitTypeId(null);
      setUnitTypeForm({ name: '', capacity: 2, bed_type: '', tags: '' });
      fetchData();
    } catch (error) {
      console.error('Update unit type error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update room type');
    }
  };

  const handleDeleteUnitType = async (unitTypeId, unitTypeName) => {
    if (!window.confirm(`Are you sure you want to delete "${unitTypeName}"? Existing rooms using this type will keep it for historical reference.`)) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/properties/${selectedProperty.id}/unit-types/${unitTypeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Room type deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete room type');
    }
  };

  // Unit/Room Update/Delete handlers
  const handleEditUnit = (unit) => {
    setEditUnitId(unit.id);
    setUnitForm({
      unit_type_id: unit.unit_type_id,
      name: unit.name,
      floor: unit.floor,
      smoking_policy: unit.smoking_policy || 'NON_SMOKING'
    });
    setUnitDialogOpen(true);
  };

  const handleUpdateUnit = async (e) => {
    e.preventDefault();
    if (!selectedProperty) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/properties/${selectedProperty.id}/units/${editUnitId}`,
        unitForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room updated successfully');
      setUnitDialogOpen(false);
      setEditUnitId(null);
      setUnitForm({ unit_type_id: '', name: '', floor: 1 });
      fetchData();
    } catch (error) {
      console.error('Update unit error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update room');
    }
  };

  const handleDeleteUnit = async (unitId, unitName) => {
    if (!window.confirm(`Are you sure you want to delete room "${unitName}"? It will disappear from the calendar and dropdowns.`)) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/properties/${selectedProperty.id}/units/${unitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Room deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  // Helper to calculate total tax
  const calculateTotalTax = () => {
    const state = parseFloat(propertyForm.tax_state_rate) || 0;
    const city = parseFloat(propertyForm.tax_city_rate) || 0;
    const county = parseFloat(propertyForm.tax_county_rate) || 0;
    const tourist = parseFloat(propertyForm.tax_tourist_rate) || 0;
    return (state + city + county + tourist).toFixed(2);
  };

  // Sort units by room number
  const sortedUnits = [...units].sort((a, b) => {
    const numA = parseInt(a.name, 10);
    const numB = parseInt(b.name, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.name.localeCompare(b.name);
  });


  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('nav.settings')}</h1>
        <p className="text-slate-600 mt-1">Manage properties, room types, and rooms</p>
      </div>

      <Tabs defaultValue="properties" className="w-full">
        <TabsList>
          <TabsTrigger value="properties" data-testid="tab-properties">Properties</TabsTrigger>
          <TabsTrigger value="room-types" data-testid="tab-room-types">Room Types</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="tab-rooms">Rooms</TabsTrigger>
        </TabsList>

        {/* Properties Tab */}
        <TabsContent value="properties" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-slate-600">{properties.length} property(ies)</p>
            <Dialog open={propertyDialogOpen} onOpenChange={(open) => {
              setPropertyDialogOpen(open);
              if (!open) {
                // Reset edit state when closing
                setEditPropertyId(null);
                setPropertyForm({ name: '', address: '', phone: '', email: '', tax_state_rate: '', tax_city_rate: '', tax_county_rate: '', tax_tourist_rate: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-property-button" onClick={() => {
                  // Ensure we're in create mode when clicking Add
                  setEditPropertyId(null);
                  setPropertyForm({ name: '', address: '', phone: '', email: '', tax_state_rate: '', tax_city_rate: '', tax_county_rate: '', tax_tourist_rate: '' });
                }}>
                  <Plus size={18} />
                  Add Property
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editPropertyId ? 'Edit Property' : 'Add New Property'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProperty} className="space-y-4" data-testid="property-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Property Name *</Label>
                      <Input
                        value={propertyForm.name}
                        onChange={(e) => setPropertyForm({...propertyForm, name: e.target.value})}
                        placeholder="Paradise Hotel"
                        required
                        data-testid="property-name-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Address</Label>
                      <Input
                        value={propertyForm.address}
                        onChange={(e) => setPropertyForm({...propertyForm, address: e.target.value})}
                        placeholder="123 Main Street, City, State"
                        data-testid="property-address-input"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={propertyForm.phone}
                        onChange={(e) => setPropertyForm({...propertyForm, phone: e.target.value})}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={propertyForm.email}
                        onChange={(e) => setPropertyForm({...propertyForm, email: e.target.value})}
                        placeholder="info@hotel.com"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-3">Tax Configuration</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>State Tax Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="99.99"
                          value={propertyForm.tax_state_rate}
                          onChange={(e) => setPropertyForm({...propertyForm, tax_state_rate: e.target.value})}
                          placeholder="6.00"
                        />
                      </div>
                      <div>
                        <Label>City Tax Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="99.99"
                          value={propertyForm.tax_city_rate}
                          onChange={(e) => setPropertyForm({...propertyForm, tax_city_rate: e.target.value})}
                          placeholder="2.00"
                        />
                      </div>
                      <div>
                        <Label>County Tax Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="99.99"
                          value={propertyForm.tax_county_rate}
                          onChange={(e) => setPropertyForm({...propertyForm, tax_county_rate: e.target.value})}
                          placeholder="1.00"
                        />
                      </div>
                      <div>
                        <Label>Tourist Tax Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="99.99"
                          value={propertyForm.tax_tourist_rate}
                          onChange={(e) => setPropertyForm({...propertyForm, tax_tourist_rate: e.target.value})}
                          placeholder="3.00"
                        />
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                      <Label className="text-sm">Total Tax Rate</Label>
                      <p className="text-2xl font-bold text-cyan-600">{calculateTotalTax()}%</p>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" data-testid="submit-property-button">
                    {editPropertyId ? 'Update Property' : 'Create Property'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="properties-list">
            {properties.map((property) => (
              <Card key={property.id} className="hover:shadow-md transition-shadow" data-testid={`property-${property.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 size={20} className="text-cyan-600" />
                      {property.name}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEditProperty(property)}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteProperty(property.id, property.name)}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {property.address && <p className="text-sm text-slate-600">{property.address}</p>}
                  {property.phone && <p className="text-sm text-slate-600 mt-1">{property.phone}</p>}
                  {property.email && <p className="text-sm text-slate-600 mt-1">{property.email}</p>}
                  {(property.tax_total_rate || property.tax_state_rate) && (
                    <p className="text-sm text-slate-600 mt-2 font-medium">
                      Total Tax: {property.tax_total_rate || calculateTotalTax()}%
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Room Types Tab */}
        <TabsContent value="room-types" className="space-y-4">
          {!selectedProperty ? (
            <p className="text-slate-500">Please select a property first</p>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <p className="text-slate-600">{unitTypes.length} room type(s)</p>
                <Dialog open={unitTypeDialogOpen} onOpenChange={(open) => {
                  setUnitTypeDialogOpen(open);
                  if (!open) {
                    setEditUnitTypeId(null);
                    setUnitTypeForm({ name: '', capacity: 2, bed_type: '', tags: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="add-unit-type-button" onClick={() => {
                      setEditUnitTypeId(null);
                      setUnitTypeForm({ name: '', capacity: 2, bed_type: '', tags: '' });
                    }}>
                      <Plus size={18} />
                      Add Room Type
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editUnitTypeId ? 'Edit Room Type' : 'Add Room Type'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUnitType} className="space-y-4" data-testid="unit-type-form">
                      <div>
                        <Label>Type Name *</Label>
                        <Input
                          value={unitTypeForm.name}
                          onChange={(e) => setUnitTypeForm({...unitTypeForm, name: e.target.value})}
                          placeholder="Deluxe King"
                          required
                          data-testid="unit-type-name-input"
                        />
                      </div>
                      <div>
                        <Label>Capacity *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={unitTypeForm.capacity}
                          onChange={(e) => setUnitTypeForm({...unitTypeForm, capacity: parseInt(e.target.value)})}
                          required
                          data-testid="unit-type-capacity-input"
                        />
                      </div>
                      <div>
                        <Label>Bed Type</Label>
                        <Input
                          value={unitTypeForm.bed_type}
                          onChange={(e) => setUnitTypeForm({...unitTypeForm, bed_type: e.target.value})}
                          placeholder="King, Queen, Twin"
                        />
                      </div>
                      <div>
                        <Label>Tags (comma-separated)</Label>
                        <Input
                          value={unitTypeForm.tags}
                          onChange={(e) => setUnitTypeForm({...unitTypeForm, tags: e.target.value})}
                          placeholder="City View, Balcony, Suite"
                        />
                      </div>
                      <Button type="submit" className="w-full" data-testid="submit-unit-type-button">
                        {editUnitTypeId ? 'Update Room Type' : 'Create Room Type'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="unit-types-list">
                {unitTypes.map((type) => (
                  <Card key={type.id} className="hover:shadow-md transition-shadow" data-testid={`unit-type-${type.id}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <BedDouble size={20} className="text-cyan-600" />
                          {type.name}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditUnitType(type)}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteUnitType(type.id, type.name)}
                          >
                            Delete
                          </Button>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600">Capacity: {type.capacity} guest(s)</p>
                      {type.bed_type && <p className="text-sm text-slate-600 mt-1">Bed: {type.bed_type}</p>}
                      {type.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {type.tags.map((tag, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-cyan-50 text-cyan-700 rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Rooms Tab */}
        <TabsContent value="rooms" className="space-y-4">
          {!selectedProperty ? (
            <p className="text-slate-500">Please select a property first</p>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <p className="text-slate-600">{units.length} room(s)</p>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setBulkRoomDialogOpen(true)}
                    variant="outline" 
                    className="gap-2"
                  >
                    <Layers size={18} />
                    Add Multiple Rooms
                  </Button>
                  <Dialog open={unitDialogOpen} onOpenChange={(open) => {
                    setUnitDialogOpen(open);
                    if (!open) {
                      setEditUnitId(null);
                      setUnitForm({ unit_type_id: '', name: '', floor: 1, smoking_policy: 'NON_SMOKING' });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button className="gap-2" data-testid="add-unit-button" onClick={() => {
                        setEditUnitId(null);
                        setUnitForm({ unit_type_id: '', name: '', floor: 1, smoking_policy: 'NON_SMOKING' });
                      }}>
                        <Plus size={18} />
                        Add Room
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editUnitId ? 'Edit Room' : 'Add Room'}</DialogTitle>
                      </DialogHeader>
                    <form onSubmit={handleCreateUnit} className="space-y-4" data-testid="unit-form">
                      <div>
                        <Label>Room Type *</Label>
                        <select
                          value={unitForm.unit_type_id}
                          onChange={(e) => setUnitForm({...unitForm, unit_type_id: e.target.value})}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          required
                          data-testid="unit-type-select"
                        >
                          <option value="">Select type</option>
                          {unitTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Room Number/Name *</Label>
                        <Input
                          value={unitForm.name}
                          onChange={(e) => setUnitForm({...unitForm, name: e.target.value})}
                          placeholder="101"
                          required
                          data-testid="unit-name-input"
                        />
                      </div>
                      <div>
                        <Label>Floor</Label>
                        <Input
                          type="number"
                          min="1"
                          value={unitForm.floor}
                          onChange={(e) => setUnitForm({...unitForm, floor: parseInt(e.target.value)})}
                          data-testid="unit-floor-input"
                        />
                      </div>
                      <div>
                        <Label>Smoking Policy *</Label>
                        <select
                          value={unitForm.smoking_policy}
                          onChange={(e) => setUnitForm({...unitForm, smoking_policy: e.target.value})}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          required
                        >
                          <option value="NON_SMOKING">Non-Smoking</option>
                          <option value="SMOKING">Smoking Allowed</option>
                        </select>
                      </div>
                      <Button type="submit" className="w-full" data-testid="submit-unit-button">
                        {editUnitId ? 'Update Room' : 'Create Room'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="units-list">
                {sortedUnits.map((unit) => {
                  const type = unitTypes.find(t => t.id === unit.unit_type_id);
                  return (
                    <Card key={unit.id} className="hover:shadow-md transition-shadow" data-testid={`unit-${unit.id}`}>
                      <CardContent className="p-4 text-center">
                        <DoorOpen size={32} className="mx-auto text-cyan-600 mb-2" />
                        <p className="text-lg font-bold text-slate-900 flex items-center justify-center gap-1">
                          {unit.name}
                          {unit.smoking_policy === 'SMOKING' && (
                            <span title="Smoking Allowed" className="text-xl">🚬</span>
                          )}
                          {unit.smoking_policy === 'NON_SMOKING' && (
                            <span title="Non-Smoking" className="text-xl">🚭</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">{type?.name || 'Unknown Type'}</p>
                        <p className="text-xs text-slate-500 mt-1">Floor {unit.floor}</p>
                        <div className="flex gap-1 mt-3">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 text-xs"
                            onClick={() => handleEditUnit(unit)}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 text-xs text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteUnit(unit.id, unit.name)}
                          >
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Bulk Room Creation Modal */}
      <BulkRoomModal
        open={bulkRoomDialogOpen}
        onClose={() => setBulkRoomDialogOpen(false)}
        propertyId={selectedProperty?.id}
        unitTypes={unitTypes}
        existingUnits={units}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default Settings;
